import { Badge } from '@ophelos/ui/components/badge';
import { cn } from '@ophelos/ui/lib/utils';
import { ArrowDown, Building, Building2, CornerDownRight, Folders, Sparkle, X } from 'lucide-react';
import type { HierarchyScope } from '@/apps/settings/lib/config-hierarchy';
import type { HierarchyLevelState } from '@/components/ai-chat/hooks/useConfigHierarchy';
import { EmptyMedia } from '@/components/ui/empty';
import { isNoDebtTypeCohort } from '@/lib/debt-type-cohort';

const SCOPE_COPY: Record<HierarchyScope, { label: string; icon: typeof Building2; unselected: string }> = {
  operator: { label: 'Operator', icon: Building, unselected: 'Your operator' },
  client: { label: 'Client', icon: Building2, unselected: 'All clients' },
  debt_type: { label: 'Debt type', icon: Folders, unselected: 'All debt types' },
};

export interface HierarchyLevelNodeProps {
  level: HierarchyLevelState;
  isEditing: boolean;
  isLast: boolean;
}

function statusFor(level: HierarchyLevelState): { text: string; variant: 'secondary' | 'subtle'; icon: typeof CornerDownRight } | null {
  if (!level.id) return null;
  if (isNoDebtTypeCohort({ id: level.id })) return { text: 'Uses the client’s settings', variant: 'subtle', icon: CornerDownRight };
  if (!level.applies) return { text: 'Cannot be changed on this page', variant: 'subtle', icon: X };
  if (level.customised) return { text: 'Custom settings', variant: 'secondary', icon: Sparkle };
  const parent = level.scope === 'client' ? 'operator' : level.scope === 'debt_type' ? 'client' : null;
  return { text: parent ? `Inherits from ${parent}` : 'Using defaults', variant: 'subtle', icon: CornerDownRight };
}

export function HierarchyLevelNode({ level, isEditing, isLast }: HierarchyLevelNodeProps) {
  const copy = SCOPE_COPY[level.scope];
  const Icon = copy.icon;
  const status = statusFor(level);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center" data-scope={level.scope}>
        <EmptyMedia variant={isEditing ? 'default' : 'subtle'}>
          <Icon />
        </EmptyMedia>
        <div className="stack gap-0.5 min-w-0 max-w-full px-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{copy.label}</p>
          <p className={cn('text-sm font-medium truncate', !isEditing && 'text-muted-foreground')}>{level.name ?? copy.unselected}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {status && (
            <Badge variant={status.variant}>
              <status.icon className="size-4" />
              {status.text}
            </Badge>
          )}
        </div>
      </div>
      {!isLast && (
        <div className="shrink-0 flex justify-center">
          <ArrowDown className="size-5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
